import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn'

/**
 * shadcn Table primitives, tuned to the Pulse data density (12.5px cells,
 * hairline row dividers, no outer border — the white card the table sits in
 * carries the structure).
 */
interface TableProps extends HTMLAttributes<HTMLTableElement> {
  containerClassName?: string
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { className, containerClassName, ...props },
  ref,
) {
  return (
    <div
      data-slot="table-container"
      className={cn('relative w-full overflow-auto', containerClassName)}
    >
      <table
        ref={ref}
        data-slot="table"
        className={cn('w-full caption-bottom border-collapse text-[12.5px]', className)}
        {...props}
      />
    </div>
  )
})

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableHeader({ className, ...props }, ref) {
  return (
    <thead
      ref={ref}
      data-slot="table-header"
      className={cn('[&_tr]:border-b [&_tr]:border-[color:var(--border)]', className)}
      {...props}
    />
  )
})

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, ...props }, ref) {
  return (
    <tbody
      ref={ref}
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  )
})

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableFooter({ className, ...props }, ref) {
  return (
    <tfoot
      ref={ref}
      data-slot="table-footer"
      className={cn(
        'border-t border-[color:var(--border)] bg-[color:var(--muted)]/50 font-medium [&>tr]:last:border-b-0',
        className,
      )}
      {...props}
    />
  )
})

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  function TableRow({ className, ...props }, ref) {
    return (
      <tr
        ref={ref}
        data-slot="table-row"
        className={cn(
          'border-b border-[color:var(--border)] transition-colors duration-[130ms]',
          'hover:bg-[color:var(--muted)]/60 data-[state=selected]:bg-[color:var(--muted)]',
          className,
        )}
        {...props}
      />
    )
  },
)

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  function TableHead({ className, ...props }, ref) {
    return (
      <th
        ref={ref}
        data-slot="table-head"
        className={cn(
          'h-9 whitespace-nowrap px-3 text-left align-middle text-[10.5px] font-semibold uppercase tracking-wider text-[color:var(--muted-foreground)]',
          '[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
          className,
        )}
        {...props}
      />
    )
  },
)

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  function TableCell({ className, ...props }, ref) {
    return (
      <td
        ref={ref}
        data-slot="table-cell"
        className={cn(
          'px-3 py-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
          className,
        )}
        {...props}
      />
    )
  },
)

export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(function TableCaption({ className, ...props }, ref) {
  return (
    <caption
      ref={ref}
      data-slot="table-caption"
      className={cn('mt-3 text-[12px] text-[color:var(--muted-foreground)]', className)}
      {...props}
    />
  )
})
